import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import TopMenu from '../../components/TopMenu';
import { useNotification } from '../../notification/NotificationContext';
import { getMyFeedback } from '../../services/mockInterviewService';
import '../../components/MockInterview/MockInterview.css';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return iso; }
};

const scoreColor = (score) => {
  if (score == null) return 'inherit';
  if (score >= 8) return '#22c55e';
  if (score >= 5) return '#f59e0b';
  return '#ef4444';
};

const scoreVerdict = (score) => {
  if (score == null) return 'Pending';
  if (score >= 8) return 'Strong';
  if (score >= 5) return 'Solid';
  return 'Work it';
};

const MyFeedbackPage = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const dark = theme === 'dark';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    getMyFeedback()
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch((e) => showNotification(e.message || 'Failed to load feedback', 'error'))
      .finally(() => setLoading(false));
  }, [showNotification]);

  if (loading) {
    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="mi-loading"><div className="mi-spin" /></div>
      </div>
    );
  }

  const avg = items.length
    ? (items.reduce((sum, f) => sum + (Number(f.score) || 0), 0) / items.length)
    : null;
  const best = items.length
    ? Math.max(...items.map((f) => Number(f.score) || 0))
    : null;

  return (
    <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="mi-ed">

        <div className="mi-ed-strip">
          <div className="mi-ed-strip-l">
            <span className="mi-ed-issue">№ 04 — Feedback Archive</span>
            <span>{items.length} session{items.length === 1 ? '' : 's'} reviewed</span>
            {avg != null && <span>Avg {avg.toFixed(1)}/10</span>}
          </div>
          <button className="mi-ed-back" onClick={() => navigate('/mock-interview')}>
            <ArrowLeft size={12} />
            Studio
          </button>
        </div>

        <div className="mi-ed-pagehead">
          <h1>
            What the <em>real ones</em> said.
            <svg
              className="mi-ed-scribble"
              viewBox="0 0 220 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M3 8 C 50 2, 110 11, 165 5 S 210 9, 217 4" />
            </svg>
          </h1>
          <p>
            Every 1-on-1 you&rsquo;ve sat through, scored and annotated.
            Tap a card to read the full notes — look for the patterns that
            keep showing up.
          </p>
        </div>

        {items.length > 0 && (
          <div className="mi-ed-meta">
            <div className="mi-ed-meta-item">
              <span className="mi-ed-meta-num">{items.length}</span>
              <span className="mi-ed-meta-lbl">sessions reviewed</span>
            </div>
            <div className="mi-ed-meta-item">
              <span className="mi-ed-meta-num">{avg != null ? avg.toFixed(1) : '—'}</span>
              <span className="mi-ed-meta-lbl">average score</span>
            </div>
            <div className="mi-ed-meta-item">
              <span className="mi-ed-meta-num">{best != null ? best.toFixed(1) : '—'}</span>
              <span className="mi-ed-meta-lbl">your best run</span>
            </div>
          </div>
        )}

        {items.length === 0 && (
          <div className="mi-ed-empty-fancy">
            <h4>No <em>feedback</em> yet.</h4>
            <p>Book a 1-on-1 — your scores and notes will land here once the call wraps.</p>
            <button
              className="mi-ed-go"
              style={{ marginTop: 18 }}
              onClick={() => navigate('/mock-interview/book')}
            >
              Book your first session
            </button>
          </div>
        )}

        {items.length > 0 && (
          <>
            <div className="mi-ed-colhead" style={{ marginTop: 36 }}>
              <span>The reviews</span>
              <em>most recent first</em>
            </div>

            <div className="mi-ed-list">
              {items.map((f, i) => {
                const isOpen = expandedId === f.id;
                const score = Number(f.score);
                const num = String(items.length - i).padStart(2, '0');
                return (
                  <div
                    key={f.id}
                    className={`mi-ed-fbcard ${isOpen ? 'mi-ed-fbcard-on' : ''}`}
                  >
                    <button
                      type="button"
                      className="mi-ed-fbhead"
                      onClick={() => setExpandedId(isOpen ? null : f.id)}
                    >
                      <span className="mi-ed-fbnum">{num}</span>
                      <div className="mi-ed-fbinfo">
                        <div className="mi-ed-fbby">
                          {f.interviewerName || 'Interviewer'}
                          {f.interviewerEmail && <em>· {f.interviewerEmail}</em>}
                        </div>
                        <div className="mi-ed-fbdate">
                          {fmtDate(f.interviewedAt || f.createdAt)} · {scoreVerdict(score)}
                        </div>
                      </div>
                      <div className="mi-ed-fbright">
                        <div className="mi-skill-bar" style={{ width: 70 }}>
                          <div
                            className="mi-skill-bar-fill"
                            style={{ width: `${(score / 10) * 100}%`, background: scoreColor(score) }}
                          />
                        </div>
                        <span className="mi-ed-fbscore" style={{ color: scoreColor(score) }}>
                          {score.toFixed(1)}
                        </span>
                        <ChevronDown
                          size={18}
                          style={{
                            opacity: 0.6,
                            transition: 'transform 0.2s ease',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="mi-ed-fbbody">
                        <h4 className="mi-ed-fbsection">What they said</h4>
                        <p style={{ lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: 0, fontSize: 14.5 }}>
                          {f.feedbackText || '—'}
                        </p>

                        {Array.isArray(f.skillRatings) && f.skillRatings.length > 0 && (
                          <>
                            <h4 className="mi-ed-fbsection">Skill breakdown</h4>
                            <div className="mi-skill-list">
                              {f.skillRatings.map((sr) => {
                                const v = Number(sr.score);
                                return (
                                  <div key={sr.skill} className="mi-skill-row">
                                    <span className="mi-skill-label">{sr.skill}</span>
                                    <div className="mi-skill-bar">
                                      <div
                                        className="mi-skill-bar-fill"
                                        style={{ width: `${(v / 10) * 100}%`, background: scoreColor(v) }}
                                      />
                                    </div>
                                    <span className="mi-skill-score" style={{ color: scoreColor(v) }}>{v}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyFeedbackPage;
